import { FC, useEffect, useMemo, useState } from 'react';

import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import {
  MeterService,
  OrderMeterReadingDto,
  OrderSolarPanelsDto,
  PreswitchBasicRecordDto,
  putEnecoBeXapiSiteApiV1OrdersByOrderIdElectricity,
} from '@eneco/dc-be/client';
import {
  Bucket,
  Button,
  Checkbox,
  Heading,
  InputMeterReading,
  InputSelect,
  NotificationBox,
  RadioButton,
  RadioGroup,
  Skeleton,
  Stack,
} from '@eneco/sparky';
import { Input } from '@eneco/sparky/components/Input/Input';
import { ElectricityIcon, SunIcon } from '@eneco/sparky/icons';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { Controller, useFormContext } from 'react-hook-form';

import DataErrorNotification from '../../../components/DataErrorNotification';
import { InputDigits } from '../../../components/InputDigits';
import ReadFormField from '../../../components/ReadFormField';
import { IsPostalCodeInWalloniaOrBrussels } from '../../../utils/addressUtils';
import { useOrderId } from '../hooks/useOrderId';
import { usePreswitchBasicRecords } from '../hooks/usePreswitchBasicRecords';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import {
  CHOOSE_START_DATE_QUESTION_NAME,
  NEW_METER_CONNECTION,
  START_DATE_QUESTION_ASAP,
  TAKE_OVER_METER_CONNECTION_OLD_INHABITANT,
} from '../utils/constants';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import { getStartDateBounds } from '../utils/orderManagementDateBounds';

export const ELEC_DETAILS_STEP_COMPENSATION_OPTIONS = {
  COMPENSATION: 'Compensation',
  INJECTION: 'Injection',
  OFFTAKE: 'Offtake',
  OFFTAKE_INJECTION: 'OfftakeInjection',
  VALORISATION: 'Valorisation',
} as const;

export const ElectricityDetailsStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { trackEventBE } = useTracking();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [hasUpdateError, setHasUpdateError] = useState(false);
  const orderId = useOrderId();
  const { matchedElectricityRecords, isLoading: isLoadingPreswitchRecords } = usePreswitchBasicRecords();
  const matchedElectricityRecord = matchedElectricityRecords.length === 1 ? matchedElectricityRecords[0] : undefined;

  const {
    register,
    trigger,
    formState: { errors },
    getValues,
    setError,
    setValue,
    watch,
    control,
  } = useFormContext<OrderManagementFormSchema>();

  useEffect(() => {
    if (!matchedElectricityRecord || isLoadingPreswitchRecords) {
      return;
    }

    setValue('electricityDetailsStep.eanCodeOfMeter', 'EAN', { shouldValidate: true });
    setValue('electricityDetailsStep.eanCode', matchedElectricityRecord.headpoint, { shouldValidate: true });

    const compensationType = mapServiceComponentToCompensationType(matchedElectricityRecord?.serviceComponent);
    if (compensationType) {
      setValue('electricityDetailsStep.compensationType', compensationType, { shouldValidate: true });
    }
  }, [matchedElectricityRecord, isLoadingPreswitchRecords, setValue]);

  const isWalloniaOrBrussels = useMemo(
    () => IsPostalCodeInWalloniaOrBrussels(getValues('deliveryAddressStep.postalCode')),
    [getValues],
  );

  const editReason = watch('electricityDetailsStep.reason');
  const startDateBounds = useMemo(() => getStartDateBounds(editReason), [editReason]);
  const meterType = watch('electricityDetailsStep.meterType');
  const startDateQuestion = watch('electricityDetailsStep.startDateQuestion');
  const eanCodeOfMeter = watch('electricityDetailsStep.eanCodeOfMeter');
  const hasExclusiveNightMeter = watch('electricityDetailsStep.hasExclusiveNightMeter');
  const eanCodeNightExclusiveOfMeter = watch('electricityDetailsStep.eanCodeNightExclusiveOfMeter');
  const meterReadingsKnown = watch('electricityDetailsStep.meterReadingsKnown');
  const meterSubType = watch('electricityDetailsStep.meterSubType');
  const hasSolarPanels = watch('electricityDetailsStep.hasSolarPanels');
  const solarPanelInstallationDate = watch('electricityDetailsStep.solarPanelInstallationDate');

  const editReasonListOptions = fields.electricityDetailsStep.editReasonList?.value.options.map(option => ({
    label: option.label,
    value: option.name,
  }));

  const startDateQuestionOptions = fields.electricityDetailsStep.startDateQuestionList?.value.options.map(option => ({
    label: option.label,
    value: option.name,
  }));

  let meterTypeListOptions = (fields.electricityDetailsStep.meterTypeList?.value.options ?? []).map(option => ({
    label: option.label,
    value: option.name,
  }));
  if (!isWalloniaOrBrussels) {
    meterTypeListOptions = meterTypeListOptions.filter(option => option.value !== 'BiDirectional');
  }

  const meterSubTypeListOptions = fields.electricityDetailsStep.meterSubTypeList?.value.options.map(option => ({
    label: option.label,
    value: option.name,
  }));

  const editReasonHint = fields.electricityDetailsStep.editReasonList?.value.options.find(
    option => option.name === 'NewMeterConnection',
  )?.description;

  const unknownMeterNumberOrEanCode = fields.electricityDetailsStep.eanCodeOfMeterList?.value.options.find(
    option => option.name === 'Unknown',
  );

  const eanCodeOfMeterListOptions = fields.electricityDetailsStep.eanCodeOfMeterList?.value.options.map(option => ({
    label: option.label,
    value: option.name,
  }));

  const solarInstallationDateOptions = fields.electricityDetailsStep.solarPanelsInstallationDateList?.value.options.map(
    option => ({
      label: option.label,
      value: option.name,
    }),
  );

  const compensationTypeOptions = useMemo(() => {
    const allOptions = fields.electricityDetailsStep.compensationTypeList?.value.options ?? [];

    if (!hasSolarPanels || !meterType || !allOptions.length) {
      return [];
    }

    const allowedNames: string[] = [];

    if (meterType === 'Analogue') {
      allowedNames.push(ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.COMPENSATION);
    } else if (meterType === 'Digital' && isWalloniaOrBrussels) {
      if (solarPanelInstallationDate === 'BeforeJanuary2024') {
        allowedNames.push(
          ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.COMPENSATION,
          ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.VALORISATION,
        );
      } else if (solarPanelInstallationDate === 'AfterJanuary2024') {
        allowedNames.push(ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.OFFTAKE_INJECTION);
      }
    } else if (meterType === 'BiDirectional') {
      allowedNames.push(
        ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.COMPENSATION,
        ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.VALORISATION,
      );
    }

    return allOptions.filter(option => allowedNames.includes(option.name));
  }, [
    hasSolarPanels,
    meterType,
    isWalloniaOrBrussels,
    solarPanelInstallationDate,
    fields.electricityDetailsStep.compensationTypeList,
  ]);

  const showWalloonSolarPanelFields = hasSolarPanels && isWalloniaOrBrussels;

  const onNextClick = async () => {
    setIsLoadingSubmit(true);
    if (!orderId) {
      setError('root', { message: 'Order ID is missing' });
      setIsLoadingSubmit(false);
      return;
    }
    const isValid = await trigger('electricityDetailsStep');
    if (!isValid) {
      setIsLoadingSubmit(false);
      return;
    }
    const values = getValues('electricityDetailsStep');
    const readings: OrderMeterReadingDto[] = [];
    if (values.dayElectricityConsumption && meterSubType === 'Single') {
      readings.push({
        meterTimeFrameType: 'TH',
        value: values.dayElectricityConsumption,
      });
    }
    if (values.nightElectricityConsumption && meterSubType === 'Multiple') {
      readings.push({
        meterTimeFrameType: 'LO',
        value: values.nightElectricityConsumption,
      });
      readings.push({
        meterTimeFrameType: 'HI',
        value: values.nightElectricityConsumption,
      });
    }
    if (values.excludingNightElectricityConsumption) {
      readings.push({
        meterTimeFrameType: 'EX',
        value: values.excludingNightElectricityConsumption,
      });
    }

    const solarPanels: OrderSolarPanelsDto = {
      hasSolarPanels: values.hasSolarPanels ?? false,
    };

    if (hasSolarPanels) {
      solarPanels.installationDate = values.solarPanelInstallationDate;
    }

    try {
      const deliveryStartDateAsap =
        values.startDateQuestion === START_DATE_QUESTION_ASAP && values.reason === 'MoveToEneco';

      const isNewMeterConnection = values.reason === NEW_METER_CONNECTION;

      const { response } = await putEnecoBeXapiSiteApiV1OrdersByOrderIdElectricity({
        path: {
          orderId: orderId ?? '',
        },
        body: {
          switchReason: values.reason!,
          ean: values.eanCode,
          meterNumber: values.meterNumber,
          exclusiveNightEan: values.eanCodeNightExclusiveCode,
          deliveryStartDate:
            deliveryStartDateAsap || isNewMeterConnection
              ? null
              : values.startDate
                ? new Date(values.startDate as Date).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
          deliveryStartDateAsap,
          meterType: values.meterType!,
          exclusiveNightMeterNumber: values.eanCodeNightExclusiveMeterNumber,
          hasExclusiveNightMeter: values.hasExclusiveNightMeter ?? false,
          readings,
          serviceComponent: values.compensationType ?? 'Offtake',
          solarPanels,
        },
      });
      if (!response.ok) {
        logger.error('Wxd7AX', 'Failed to update electricity details', { response });
        setHasUpdateError(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsLoadingSubmit(false);
        trackEventBE('be_om_electricity_details', {
          status: 'failed',
          message: 'Failed to update order with electricity details',
          data: getTrackableFormValues(getValues()),
        });
        return;
      }
      trackEventBE('be_om_electricity_details', {
        status: 'success',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    } catch {
      trackEventBE('be_om_electricity_details', {
        status: 'failed',
        message: 'Failed to update order with electricity details',
        data: getTrackableFormValues(getValues()),
      });
      setHasUpdateError(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <Stack gap="10" alignX="start">
      {hasUpdateError && <DataErrorNotification />}
      <Stack gap="2">
        <Heading as="h2" size="M">
          {fields.electricityDetailsStep.title.value}
        </Heading>
        <RichText html={fields.electricityDetailsStep.subTitle.value} />
      </Stack>
      <Bucket title={fields.electricityDetailsStep.reasonStartDateTitle.value}>
        <Bucket.Content>
          <InputSelect
            label={fields.electricityDetailsStep.editReasonList.value.label}
            options={editReasonListOptions}
            isOptional={false}
            error={errors.electricityDetailsStep?.reason?.message}
            placeholder={''}
            hint={editReason === 'NewMeterConnection' ? <RichText html={editReasonHint} /> : null}
            {...register('electricityDetailsStep.reason')}
          />
          {editReason === 'MoveToEneco' && (
            <InputSelect
              label={fields.electricityDetailsStep.startDateQuestionList.value.label}
              options={startDateQuestionOptions}
              isOptional={false}
              error={errors.electricityDetailsStep?.startDateQuestion?.message}
              placeholder={''}
              {...register('electricityDetailsStep.startDateQuestion')}
            />
          )}
          {((startDateQuestion === CHOOSE_START_DATE_QUESTION_NAME && editReason !== NEW_METER_CONNECTION) ||
            editReason === TAKE_OVER_METER_CONNECTION_OLD_INHABITANT) && (
            <Input
              label={fields.electricityDetailsStep.startDateFormField.value.label}
              id="startDate"
              type="date"
              min={startDateBounds.min}
              max={startDateBounds.max}
              error={errors.electricityDetailsStep?.startDate?.message}
              {...register('electricityDetailsStep.startDate')}
              autoComplete="off"
            />
          )}
        </Bucket.Content>
      </Bucket>
      <Bucket
        title={
          <Stack gap="2" direction="row" alignY="center">
            <ElectricityIcon color="iconElectricity" />
            <Heading as="h3" size="3XS">
              {fields.electricityDetailsStep.meterInformationTitle.value}
            </Heading>
          </Stack>
        }>
        <Bucket.Content>
          <MeterInformationFields
            fields={fields}
            matchedElectricityRecord={matchedElectricityRecord}
            isLoadingPreswitchRecords={isLoadingPreswitchRecords}
            meterTypeListOptions={meterTypeListOptions}
            eanCodeOfMeterListOptions={eanCodeOfMeterListOptions}
            eanCodeOfMeter={eanCodeOfMeter}
            hasExclusiveNightMeter={hasExclusiveNightMeter}
            eanCodeNightExclusiveOfMeter={eanCodeNightExclusiveOfMeter}
            unknownMeterNumberOrEanCode={unknownMeterNumberOrEanCode}
          />
          {editReason === 'TakeOverMeterConnectionOldInhabitant' && meterType === 'Analogue' && (
            <>
              <Checkbox
                label={fields.electricityDetailsStep.meterReadingsKnownFormField.value.label}
                error={errors.electricityDetailsStep?.meterReadingsKnown?.message}
                {...register('electricityDetailsStep.meterReadingsKnown')}
                hint={<RichText html={fields.electricityDetailsStep.meterReadingsKnownFormField.value.hint} />}
              />
              {meterReadingsKnown && (
                <>
                  <InputSelect
                    label={fields.electricityDetailsStep.meterSubTypeList.value.label}
                    options={meterSubTypeListOptions}
                    isOptional={false}
                    error={errors.electricityDetailsStep?.meterSubType?.message}
                    placeholder={fields.electricityDetailsStep.meterSubTypeList.value.hint}
                    {...register('electricityDetailsStep.meterSubType')}
                  />
                  <InputMeterReading
                    label={fields.electricityDetailsStep.dayElectricityConsumptionFormField.value.label}
                    unit="kWh"
                    maximumFractionDigits={0}
                    error={errors.electricityDetailsStep?.dayElectricityConsumption?.message}
                    placeholder={fields.electricityDetailsStep.dayElectricityConsumptionFormField.value.placeholder}
                    {...register('electricityDetailsStep.dayElectricityConsumption')}
                    hint={
                      <RichText html={fields.electricityDetailsStep.dayElectricityConsumptionFormField.value.hint} />
                    }
                  />
                  {meterSubType === 'Multiple' && (
                    <InputMeterReading
                      label={fields.electricityDetailsStep.nightElectricityConsumptionFormField.value.label}
                      unit="kWh"
                      maximumFractionDigits={0}
                      error={errors.electricityDetailsStep?.nightElectricityConsumption?.message}
                      placeholder={fields.electricityDetailsStep.nightElectricityConsumptionFormField.value.placeholder}
                      {...register('electricityDetailsStep.nightElectricityConsumption')}
                      hint={
                        <RichText
                          html={fields.electricityDetailsStep.excludingNightElectricityConsumptionFormField.value.hint}
                        />
                      }
                    />
                  )}
                  {hasExclusiveNightMeter && (
                    <InputMeterReading
                      label={fields.electricityDetailsStep.excludingNightElectricityConsumptionFormField.value.label}
                      unit="kWh"
                      maximumFractionDigits={0}
                      error={errors.electricityDetailsStep?.excludingNightElectricityConsumption?.message}
                      placeholder={
                        fields.electricityDetailsStep.excludingNightElectricityConsumptionFormField.value.placeholder
                      }
                      {...register('electricityDetailsStep.excludingNightElectricityConsumption')}
                      hint={
                        <RichText
                          html={fields.electricityDetailsStep.excludingNightElectricityConsumptionFormField.value.hint}
                        />
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
        </Bucket.Content>
      </Bucket>
      <Bucket
        title={
          <Stack gap="2" direction="row" alignY="center">
            <SunIcon color="iconSolar" />
            <Heading as="h3" size="3XS">
              {fields.electricityDetailsStep.solarPanelsTitle.value}
            </Heading>
          </Stack>
        }>
        <Bucket.Content>
          <Checkbox
            label={fields.electricityDetailsStep.hasSolarPanelsFormField.value.label}
            error={errors.electricityDetailsStep?.hasSolarPanels?.message}
            {...register('electricityDetailsStep.hasSolarPanels')}
            hint={<RichText html={fields.electricityDetailsStep.hasSolarPanelsFormField.value.hint} />}
          />
          {showWalloonSolarPanelFields && (
            <>
              {meterType === 'Digital' && (
                <Controller
                  control={control}
                  name="electricityDetailsStep.solarPanelInstallationDate"
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup
                      name="electricityDetailsStep.solarPanelInstallationDate"
                      label={fields.electricityDetailsStep.solarPanelsInstallationDateList.value.label}
                      direction="column"
                      value={value?.toString()}
                      error={errors.electricityDetailsStep?.solarPanelInstallationDate?.message}
                      onValueChange={newValue => {
                        onChange(newValue);
                      }}>
                      {solarInstallationDateOptions.map(option => (
                        <RadioButton key={option?.value} value={option?.value}>
                          {option.label}
                        </RadioButton>
                      ))}
                    </RadioGroup>
                  )}
                />
              )}
              {solarPanelInstallationDate && !matchedElectricityRecord && compensationTypeOptions.length > 1 && (
                <InputSelect
                  label={fields.electricityDetailsStep.compensationTypeList.value.label}
                  options={compensationTypeOptions.map(option => ({
                    label: option.label,
                    value: option.name,
                  }))}
                  isOptional={false}
                  error={errors.electricityDetailsStep?.compensationType?.message}
                  placeholder={''}
                  {...register('electricityDetailsStep.compensationType')}
                />
              )}
              {solarPanelInstallationDate && !matchedElectricityRecord && compensationTypeOptions.length === 1 && (
                <ReadFormField
                  key={compensationTypeOptions[0].value}
                  label={fields.electricityDetailsStep.compensationTypeList.value.label}
                  value={compensationTypeOptions[0].label}
                  {...register('electricityDetailsStep.compensationType')}
                />
              )}
            </>
          )}
        </Bucket.Content>
      </Bucket>
      {errors.root && <NotificationBox isAlert={true} variant="error" text={errors.root.message} />}
      <Button onClick={onNextClick} isLoading={isLoadingSubmit}>
        {fields.navigation.nextButtonLabel.value}
      </Button>
    </Stack>
  );
};

interface MeterInformationFieldsProps {
  fields: OrderManagementWithItems['fields'];
  matchedElectricityRecord?: PreswitchBasicRecordDto;
  isLoadingPreswitchRecords: boolean;
  meterTypeListOptions: Array<{ label: string; value: string }>;
  eanCodeOfMeterListOptions: Array<{ label: string; value: string }>;
  eanCodeOfMeter: string | undefined;
  hasExclusiveNightMeter: boolean | undefined;
  eanCodeNightExclusiveOfMeter: string | undefined;
  unknownMeterNumberOrEanCode?: { description: string };
}

const MeterInformationFields: FC<MeterInformationFieldsProps> = ({
  fields,
  matchedElectricityRecord,
  isLoadingPreswitchRecords,
  meterTypeListOptions,
  eanCodeOfMeterListOptions,
  eanCodeOfMeter,
  hasExclusiveNightMeter,
  eanCodeNightExclusiveOfMeter,
  unknownMeterNumberOrEanCode,
}) => {
  const {
    formState: { errors },
    register,
  } = useFormContext<OrderManagementFormSchema>();
  if (isLoadingPreswitchRecords) {
    return (
      <Stack gap={4}>
        <Skeleton height={100} />
        <Skeleton height={100} />
        <Skeleton height={100} />
      </Stack>
    );
  }

  if (matchedElectricityRecord) {
    return (
      <>
        <ReadFormField
          label={fields.electricityDetailsStep.eanCodeFormField.value.label}
          value={matchedElectricityRecord.headpoint}
          isLoading={isLoadingPreswitchRecords}
        />
        {matchedElectricityRecord?.serviceComponent && (
          <ReadFormField
            label={fields.electricityDetailsStep.compensationTypeList.value.label}
            value={mapServiceComponentToCompensationType(matchedElectricityRecord?.serviceComponent) ?? ''}
            isLoading={isLoadingPreswitchRecords}
          />
        )}
      </>
    );
  }

  return (
    <>
      <InputSelect
        label={fields.electricityDetailsStep.meterTypeList.value.label}
        options={meterTypeListOptions}
        isOptional={false}
        error={errors.electricityDetailsStep?.meterType?.message}
        placeholder={''}
        {...register('electricityDetailsStep.meterType')}
      />
      <InputSelect
        label={fields.electricityDetailsStep.eanCodeOfMeterList.value.label}
        options={eanCodeOfMeterListOptions}
        isOptional={false}
        error={errors.electricityDetailsStep?.eanCodeOfMeter?.message}
        placeholder={''}
        hint={
          eanCodeOfMeter === 'Unknown' ? (
            <u>
              <RichText html={unknownMeterNumberOrEanCode?.description} />
            </u>
          ) : null
        }
        {...register('electricityDetailsStep.eanCodeOfMeter')}
      />
      {eanCodeOfMeter === 'EAN' && (
        <InputDigits
          label={fields.electricityDetailsStep.eanCodeFormField.value.label}
          error={errors.electricityDetailsStep?.eanCode?.message}
          placeholder={fields.electricityDetailsStep.eanCodeFormField.value.placeholder}
          {...register('electricityDetailsStep.eanCode')}
          hint={<RichText html={fields.electricityDetailsStep.eanCodeFormField?.value.hint} />}
        />
      )}
      {eanCodeOfMeter === 'MeterNumber' && (
        <Input
          label={fields.electricityDetailsStep.meterNumberFormField.value.label}
          id="meterNumber"
          type="text"
          error={errors.electricityDetailsStep?.meterNumber?.message}
          placeholder={fields.electricityDetailsStep.meterNumberFormField.value.placeholder}
          {...register('electricityDetailsStep.meterNumber')}
          hint={<RichText html={fields.electricityDetailsStep.meterNumberFormField.value.hint} />}
        />
      )}
      <Checkbox
        label={fields.electricityDetailsStep.hasExclusiveNightMeterFormField.value.label}
        error={errors.electricityDetailsStep?.hasExclusiveNightMeter?.message}
        hint={<RichText html={fields.electricityDetailsStep.hasExclusiveNightMeterFormField.value.hint} />}
        {...register('electricityDetailsStep.hasExclusiveNightMeter')}
      />
      {hasExclusiveNightMeter && (
        <>
          <InputSelect
            label={fields.electricityDetailsStep.eanCodeOfNightMeterFormField.value.label}
            options={eanCodeOfMeterListOptions}
            isOptional={false}
            error={errors.electricityDetailsStep?.eanCodeNightExclusiveOfMeter?.message}
            placeholder={''}
            {...register('electricityDetailsStep.eanCodeNightExclusiveOfMeter')}
            hint={
              eanCodeNightExclusiveOfMeter === 'Unknown' ? (
                <u>
                  <RichText html={unknownMeterNumberOrEanCode?.description} />
                </u>
              ) : null
            }
          />
          {eanCodeNightExclusiveOfMeter === 'EAN' && (
            <InputDigits
              label={fields.electricityDetailsStep.eanCodeNightExclusiveFormField.value.label}
              error={errors.electricityDetailsStep?.eanCodeNightExclusiveCode?.message}
              placeholder={fields.electricityDetailsStep.eanCodeFormField.value.placeholder}
              {...register('electricityDetailsStep.eanCodeNightExclusiveCode')}
              hint={<RichText html={fields.electricityDetailsStep.eanCodeFormField.value.hint} />}
            />
          )}
          {eanCodeNightExclusiveOfMeter === 'MeterNumber' && (
            <Input
              label={fields.electricityDetailsStep.meterNumberFormField.value.label}
              id="meterNumber"
              type="text"
              error={errors.electricityDetailsStep?.eanCodeNightExclusiveMeterNumber?.message}
              placeholder={fields.electricityDetailsStep.meterNumberFormField.value.placeholder}
              {...register('electricityDetailsStep.eanCodeNightExclusiveMeterNumber')}
              hint={<RichText html={fields.electricityDetailsStep.meterNumberFormField.value.hint} />}
            />
          )}
        </>
      )}
    </>
  );
};

type ElectricityCompensationType =
  (typeof ELEC_DETAILS_STEP_COMPENSATION_OPTIONS)[keyof typeof ELEC_DETAILS_STEP_COMPENSATION_OPTIONS];

const mapServiceComponentToCompensationType = (
  serviceComponent: MeterService | undefined,
): ElectricityCompensationType | undefined => {
  if (!serviceComponent) return undefined;
  switch (serviceComponent) {
    case 'Compensation':
      return ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.COMPENSATION;
    case 'PureInjection':
    case 'CommercialisationInjection':
    case 'CommercialisationConstraintInjection':
    case 'ValorisationInjection':
      return ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.INJECTION;
    case 'PureOfftake':
    case 'CompensatedOfftake':
      return ELEC_DETAILS_STEP_COMPENSATION_OPTIONS.OFFTAKE;
    default:
      return undefined;
  }
};
